/**
 * @fileoverview useProgressAnimation - Single source of truth for progress animation
 * @module screen-view/hooks/useProgressAnimation
 *
 * Provides a unified RAF-based animation loop that drives:
 * - progress (0-1) - the main value everything derives from
 * - percent (0-100) - rounded percentage for display
 * - itemIndex - which slide/item to show based on durationPercent
 *
 * Replaces the previous 3-mechanism approach:
 * - CSS @keyframes (circle fill)
 * - RAF loop (AnimatedProgressLabel)
 * - setTimeout chain (slide changes)
 *
 * Now all three are perfectly synchronized from one progress value.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Item with optional durationPercent for calculating itemIndex
 */
export interface ProgressAnimationItem {
  durationPercent?: number;
}

/**
 * Options for useProgressAnimation hook
 */
export interface UseProgressAnimationOptions {
  /** Total animation duration in milliseconds */
  duration: number;
  /** Items with durationPercent for calculating itemIndex */
  items?: ProgressAnimationItem[];
  /** Start animation immediately on mount (default: false) */
  autoStart?: boolean;
  /** Called once when progress reaches 1 (100%) */
  onComplete?: () => void;
}

/**
 * Result returned by useProgressAnimation hook
 */
export interface UseProgressAnimationResult {
  /** Progress value 0-1 (main source of truth) */
  progress: number;
  /** Percentage 0-100 (rounded) for display */
  percent: number;
  /** Current item index based on progress and item durationPercents */
  itemIndex: number;
  /** True when animation is running */
  isPlaying: boolean;
  /** True when progress has reached 1 */
  isComplete: boolean;
  /** Start or resume animation */
  play: () => void;
  /** Pause animation at current progress */
  pause: () => void;
  /** Reset to 0 and start playing */
  restart: () => void;
}

/**
 * Calculate cumulative breakpoints from items' durationPercent values.
 * Returns array of breakpoints [0, b1, b2, ..., 1] where each bi
 * represents the progress value where item i ends.
 *
 * @example
 * items = [{ durationPercent: 30 }, { durationPercent: 40 }, { durationPercent: 30 }]
 * returns [0, 0.3, 0.7, 1.0]
 */
function calculateBreakpoints(items: ProgressAnimationItem[]): number[] {
  if (items.length === 0) return [0, 1];

  // Check if items have valid durationPercent values
  const hasValidDurations = items.every(
    (item) => typeof item.durationPercent === 'number' && item.durationPercent > 0
  );

  let durations: number[];
  if (hasValidDurations) {
    // Use provided durationPercent values
    const total = items.reduce((sum, item) => sum + (item.durationPercent ?? 0), 0);
    durations = items.map((item) => (item.durationPercent ?? 0) / total);
  } else {
    // Distribute evenly
    const evenDuration = 1 / items.length;
    durations = items.map(() => evenDuration);
  }

  // Build cumulative breakpoints
  const breakpoints: number[] = [0];
  let cumulative = 0;
  for (const duration of durations) {
    cumulative += duration;
    breakpoints.push(cumulative);
  }

  // Ensure last breakpoint is exactly 1
  breakpoints[breakpoints.length - 1] = 1;

  return breakpoints;
}

/**
 * Find item index for a given progress value using breakpoints.
 *
 * @param progress - Current progress value (0-1)
 * @param breakpoints - Cumulative breakpoints array
 * @returns Index of the item that should be displayed
 */
function findItemIndex(progress: number, breakpoints: number[]): number {
  // Handle edge cases
  if (breakpoints.length <= 2) return 0;
  if (progress <= 0) return 0;
  if (progress >= 1) return breakpoints.length - 2; // Last item index

  // Find which segment progress falls into
  for (let i = 1; i < breakpoints.length; i++) {
    if (progress < breakpoints[i]) {
      return i - 1;
    }
  }

  return breakpoints.length - 2; // Fallback to last item
}

/**
 * useProgressAnimation - Single RAF loop driving all progress-related values
 *
 * @example
 * const { progress, percent, itemIndex, isPlaying, play, pause, restart } = useProgressAnimation({
 *   duration: 15000,
 *   items: progressBar.items,
 *   onComplete: () => console.log('Animation complete'),
 * });
 *
 * // In render:
 * <CircleProgressBar progress={progress} />
 * <span>{percent}%</span>
 * <SlideContent item={items[itemIndex]} />
 * <Button onClick={isPlaying ? pause : play}>{isPlaying ? 'Pause' : 'Play'}</Button>
 */
export function useProgressAnimation({
  duration,
  items = [],
  autoStart = false,
  onComplete,
}: UseProgressAnimationOptions): UseProgressAnimationResult {
  // Core state - always start paused, let autoStart effect handle starting
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Refs for RAF management
  const rafIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedElapsedRef = useRef<number>(0);
  const onCompleteCalledRef = useRef(false);

  // Calculate breakpoints from items (memoized via dependency)
  const breakpointsRef = useRef<number[]>([]);
  useEffect(() => {
    breakpointsRef.current = calculateBreakpoints(items);
  }, [items]);

  // Derived values
  const percent = Math.round(progress * 100);
  const itemIndex = findItemIndex(progress, breakpointsRef.current);

  /**
   * Core animation loop
   */
  const animate = useCallback(
    (timestamp: number) => {
      if (startTimeRef.current === null) {
        // First frame - account for any paused time
        startTimeRef.current = timestamp - pausedElapsedRef.current;
      }

      const elapsed = timestamp - startTimeRef.current;
      const newProgress = Math.min(elapsed / duration, 1);

      setProgress(newProgress);

      if (newProgress >= 1) {
        // Animation complete
        setIsPlaying(false);
        setIsComplete(true);

        // Fire onComplete callback once
        if (!onCompleteCalledRef.current && onComplete) {
          onCompleteCalledRef.current = true;
          onComplete();
        }

        rafIdRef.current = null;
      } else {
        // Continue animation
        rafIdRef.current = requestAnimationFrame(animate);
      }
    },
    [duration, onComplete]
  );

  /**
   * Start or resume animation
   */
  const play = useCallback(() => {
    if (isPlaying) return; // Already playing

    // If complete, don't auto-restart - use restart() instead
    if (isComplete) return;

    setIsPlaying(true);
    startTimeRef.current = null; // Will be set on first frame with pausedElapsed offset
    rafIdRef.current = requestAnimationFrame(animate);
  }, [isPlaying, isComplete, animate]);

  /**
   * Pause animation at current progress
   */
  const pause = useCallback(() => {
    if (!isPlaying) return; // Already paused

    // Cancel RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Store elapsed time for resume
    if (startTimeRef.current !== null) {
      pausedElapsedRef.current = performance.now() - startTimeRef.current;
    }

    setIsPlaying(false);
  }, [isPlaying]);

  /**
   * Reset to 0 and start playing
   */
  const restart = useCallback(() => {
    // Cancel any existing animation
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Reset all state
    setProgress(0);
    setIsComplete(false);
    setIsPlaying(true);
    startTimeRef.current = null;
    pausedElapsedRef.current = 0;
    onCompleteCalledRef.current = false;

    // Start animation
    rafIdRef.current = requestAnimationFrame(animate);
  }, [animate]);

  // Auto-start effect - only runs once on mount if autoStart is true
  const hasAutoStarted = useRef(false);
  useEffect(() => {
    if (autoStart && !hasAutoStarted.current) {
      hasAutoStarted.current = true;
      play();
    }
  }, [autoStart, play]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return {
    progress,
    percent,
    itemIndex,
    isPlaying,
    isComplete,
    play,
    pause,
    restart,
  };
}

export default useProgressAnimation;
